import React from "react";
import styles from "./InfoBoxesModule.module.scss";
import { InfoboxesOptionsType } from "@/types/ModuleOptionsType";
import { columnSize } from "../../utils/layout/columnSize";
import { TextInfoBox } from "./TextInfoBox/TextInfoBox";
import { InfoBoxes } from "./InfoBoxes/InfoBoxes";
import { Modul_Infoboxen } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { LinkType } from "@/types/baseTypes";
import { getItemWidthBreakpoints } from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { ExtendedLinkType } from "@/components/ExtededLink/ExtendedLink";
import { Headline } from "@/components/Headline/Headline";
import { Section } from "@/components/Section/Section";

export type InfoBoxesModuleType = Modul_Infoboxen;

export type InfoBoxElementType = {
  title: string;
  infobox?: string;
  link: LinkType;
  link_extended: ExtendedLinkType;
};

export type TextInfoBoxType = InfoBoxesModuleType["infoboxes"]["text_infobox"];

interface InfoBoxesModuleProps {
  data: InfoBoxesModuleType;
  infoBoxOptions: InfoboxesOptionsType;
}
const isInfoboxesModule = (
  data: InfoBoxesModuleType,
): data is InfoBoxesModuleType => {
  return "infoboxes" in data;
};

const InfoBoxesModule = ({ infoBoxOptions, data }: InfoBoxesModuleProps) => {
  const isInfoboxes = isInfoboxesModule(data);

  //Determine if the module is an infoboxes module or an icon grid module
  const content = data.infoboxes;

  const { headline, headline_html_tag, overline, subline } = content;
  const { type, text_infobox } = data.infoboxes;

  const elements = content.elements || [];

  const {
    id,
    background_color_class,
    section_margin_class,
    section_padding_class,
    module_width,
    row_direction_class,
    headline_text_align_class,
    text_align_class,
    grid_columns,
    toggle_cardbox,
    trenner_anzeigen,
  } = data.layout;

  const globalShowBox = infoBoxOptions?.box?.show_card_box ?? false;
  const showCardbox = globalShowBox ? toggle_cardbox : false;

  const columns_vertical_align_class = data.layout.columns_vertical_align_class;

  const { show_overline_subline, columns, grid_columns_choices } =
    infoBoxOptions ?? {
      show_overline_subline: "none",
      columns: { breakpoint: "tablet", number_of_cols: 12 },
      grid_columns_choices: {},
    };

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  const columnSizes =
    module_width !== "normal-frame"
      ? columns
        ? columnSize(columns)
        : "is-12"
      : "is-12";

  const fallbackItemWidth = {
    mobile: "100%",
    tablet: "50%",
    desktop: "50%",
    widescreen: "50%",
    fullhd: "50%",
    ultrahd: "50%",
  };

  const itemWidth = getItemWidthBreakpoints(
    grid_columns_choices,
    grid_columns,
    fallbackItemWidth,
  );

  const tabletDirection =
    row_direction_class === "is-row-reverse-desktop"
      ? "is-row-reverse-tablet"
      : "";

  const headlineJSX = (
    <Headline
      headline={headline}
      headline_html_tag={headline_html_tag === "h3" ? "h3" : "h2"} // todo: fix h2 bug
      overline={showOverline ? overline : ""}
      subline={showSubline ? subline : ""}
      extraClass={headline_text_align_class}
      wrapInSection={module_width === "frameless"}
      disableAnimation
    />
  );

  const infoBoxesJSX = (
    <div className="columns is-multiline is-centered">
      <div className={`column is-12 ${styles["text-infobox"]}`}>
        {isInfoboxes && type === "text-infobox" && text_infobox && (
          <TextInfoBox
            text_infobox={text_infobox}
            columnsClass={`${tabletDirection} ${columns_vertical_align_class}`}
            fitHeight={
              columns_vertical_align_class === "is-vcentered" ? false : true
            }
            infoBoxOptions={infoBoxOptions}
            extraClass={`${text_align_class} py-0`}
            headline={headlineJSX}
            show_cardbox={showCardbox}
            show_divider={trenner_anzeigen}
            heading_level={headline && headline_html_tag === "h3" ? 4 : 3}
          />
        )}

        {(!isInfoboxes || type !== "text-infobox") && (
          <InfoBoxes
            elements={elements}
            infoBoxOptions={infoBoxOptions}
            extraClass={
              isInfoboxes
                ? `${text_align_class}`
                : `${text_align_class}  ${tabletDirection}`
            }
            itemWidth={itemWidth}
            show_cardbox={showCardbox}
            show_divider={trenner_anzeigen}
            heading_level={headline && headline_html_tag === "h3" ? 4 : 3}
            layout="v2"
            type="infobox"
          />
        )}
      </div>
    </div>
  );

  return (
    <Section
      id={id}
      className={`${isInfoboxes ? "info-boxes" : "icon-grid"} ${styles["info-boxes"]}  ${isInfoboxes && type === "text-infobox" ? `${styles[type]}` : ""} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      disableColumns
      frameless={module_width === "frameless"}
    >
      {module_width === "narrow-frame" ? (
        <div className="columns is-centered">
          <div className={`column ${columnSizes} is-centered`}>
            {infoBoxesJSX}
          </div>
        </div>
      ) : (
        infoBoxesJSX
      )}
    </Section>
  );
};

export default InfoBoxesModule;
